import React from "react";
import { FaChevronRight, FaCopy, FaEnvelope, FaPhone, FaUserFriends, FaUsers } from "react-icons/fa";
import ChildEmptyState from "../../shared/components/ChildEmptyState";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") ?? "";

const fullName = (person = {}) =>
  pickFirst(
    person.fullName,
    person.name,
    [person.firstName, person.lastName].filter(Boolean).join(" "),
    [person.firstname, person.lastname].filter(Boolean).join(" ")
  );

const initialsFor = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("") || "?";

const normalizeParents = (family) => {
  if (!family) return [];

  if (Array.isArray(family.parents)) {
    return family.parents
      .map((parent, index) => ({
        id: parent.id ?? parent.uuid ?? parent.parentId ?? null,
        name: fullName(parent),
        email: pickFirst(parent.email, parent.emailAddress, parent.mail),
        phone: pickFirst(parent.phone, parent.phoneNumber, parent.mobile, parent.mobilePhone, parent.telephone),
        labelIndex: index + 1,
      }))
      .filter((parent) => parent.name || parent.email || parent.phone);
  }

  return ["parent1", "parent2"]
    .map((key, index) => {
      const parentObject = family[key] || {};
      return {
        id: pickFirst(family[`${key}Id`], parentObject.id, parentObject.uuid),
        name: pickFirst(
          fullName(parentObject),
          [family[`${key}FirstName`], family[`${key}LastName`]].filter(Boolean).join(" "),
          [family[`${key}Firstname`], family[`${key}Lastname`]].filter(Boolean).join(" ")
        ),
        email: pickFirst(
          family[`${key}Email`],
          family[`${key}EmailAddress`],
          parentObject.email,
          parentObject.emailAddress,
          parentObject.mail
        ),
        phone: pickFirst(
          family[`${key}Phone`],
          family[`${key}PhoneNumber`],
          family[`${key}Mobile`],
          parentObject.phone,
          parentObject.phoneNumber,
          parentObject.mobile,
          parentObject.mobilePhone,
          parentObject.telephone
        ),
        labelIndex: index + 1,
      };
    })
    .filter((parent) => parent.name || parent.email || parent.phone);
};

const copyToClipboard = async (value) => {
  if (!value || typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
  try {
    await navigator.clipboard.writeText(String(value));
  } catch {
    // Ignore clipboard failures; the contact link remains available.
  }
};

const ContactLine = ({ icon: Icon, value, emptyLabel, href }) => {
  const content = value || emptyLabel;
  const className = `flex min-w-0 items-center gap-2 text-sm ${
    value ? "text-slate-600 dark:text-slate-300" : "text-slate-400 dark:text-slate-500"
  }`;

  return (
    <div className="flex min-w-0 items-center gap-2">
      {href && value ? (
        <a href={href} className={`${className} min-w-0 flex-1 transition hover:text-indigo-700 dark:hover:text-indigo-200`}>
          <Icon className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{content}</span>
        </a>
      ) : (
        <div className={`${className} min-w-0 flex-1`}>
          <Icon className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{content}</span>
        </div>
      )}

      {value ? (
        <button
          type="button"
          onClick={() => copyToClipboard(value)}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-indigo-400/40 dark:hover:bg-indigo-400/10 dark:hover:text-indigo-200"
          aria-label="Copy"
          title="Copy"
        >
          <FaCopy className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export default function ChildFamilyDetailsPanel({ families = [], labels, onOpenFamily }) {
  const primaryFamily = families[0] || null;
  const parents = normalizeParents(primaryFamily);

  return (
    <section className="rounded-[20px] border border-slate-200/80 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-200">
          <FaUsers aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-xl font-bold text-slate-950 dark:text-white">{labels.title}</h3>
          {primaryFamily?.familyName && (
            <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-300">{primaryFamily.familyName}</p>
          )}
        </div>
      </div>

      {!primaryFamily ? (
        <ChildEmptyState title={labels.noFamily} />
      ) : (
        <div className="rounded-[18px] border border-sky-100 bg-sky-50/55 p-4 dark:border-sky-400/20 dark:bg-sky-400/10">
          {parents.length > 0 ? (
            <div className="space-y-3">
              {parents.map((parent) => (
                <div
                  key={`${parent.labelIndex}-${parent.id || parent.name || parent.email}`}
                  className="rounded-[16px] border border-white/80 bg-white/85 p-4 shadow-sm dark:border-white/10 dark:bg-white/5"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-bold text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-100">
                      {initialsFor(parent.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">
                        {labels.parent} {parent.labelIndex}
                      </p>
                      <p className="mt-1 break-words text-base font-bold text-slate-950 dark:text-white">
                        {parent.name || labels.unknownParent}
                      </p>
                      <div className="mt-3 grid gap-2">
                        <ContactLine
                          icon={FaEnvelope}
                          value={parent.email}
                          emptyLabel={labels.noEmail}
                          href={parent.email ? `mailto:${parent.email}` : null}
                        />
                        <ContactLine
                          icon={FaPhone}
                          value={parent.phone}
                          emptyLabel={labels.noPhone}
                          href={parent.phone ? `tel:${String(parent.phone).replace(/\s+/g, "")}` : null}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ChildEmptyState title={labels.noParents} description={labels.noParentsDescription} />
          )}

          <button
            type="button"
            onClick={() => onOpenFamily(primaryFamily)}
            className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full border border-sky-200 bg-white px-5 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
          >
            <FaUserFriends aria-hidden="true" />
            {labels.moreInfo}
            <FaChevronRight aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}
